import { Button } from "@jetblue/kit/button";
import { InputCheckbox } from "@jetblue/kit/input-checkbox";
import { InputRoute } from "@jetblue/dotcom-components/input-route";
import { InputText } from "@jetblue/kit/input-text";

import type { FlightSearchFormLabels } from "../labels";
import type { StatelessFlightSearchFormProperties } from "../stateless";
import type { TripType } from "../hooks/use-flight-search-form";
import type { PassengerCounts } from "../use-passengers";
import type { useForm } from "../use-form";
import { BestFaresRangeDatePicker } from "../components/best-fares/range-date-picker";
import { InputSelect, useSelect } from "@jetblue/kit/input-select";

type RoundTripErrors = {
  hasErrors: boolean;
  setShowValidation: (showValidation: boolean) => void;
  showValidation: boolean;
};

export type RoundTripProperties = {
  bestFareFinderBaseUrl?: string | undefined;
  errors: RoundTripErrors;
  formList: ReturnType<typeof useForm>;
  isOpen?: boolean | undefined;
  onOpenChange?: ((isOpen: boolean) => void) | undefined;
  labels: FlightSearchFormLabels;
  odQuery: StatelessFlightSearchFormProperties["odQuery"];
  onTripTypeChange: (value: TripType | null) => void;
  passengers: PassengerCounts;
  showDatePickerPricing: boolean;
  showPromoCode?: boolean | undefined;
};
export const RoundTrip = ({
  bestFareFinderBaseUrl,
  errors,
  onTripTypeChange,
  formList: {
    endDate,
    first: { departureDate, destination, origin, returnDate },
    modify,
    promoCode,
    setPromoCode,
    setUsePoints,
    startDate,
    swapOriginDestination,
    usePoints,
  },
  isOpen,
  onOpenChange,
  labels,
  odQuery,
  passengers,
  showDatePickerPricing,
  showPromoCode,
}: RoundTripProperties) => (
  <div
    className="
      flex flex-col gap-1

      lg:flex-row
    "
  >
    <InputRoute
      className="flex-1"
      data-fs-element="route"
      destination={destination}
      destinationIsRequired={true}
      destinationName="to"
      labels={labels}
      odRouteData={{ destination: odQuery.data, origin: odQuery.data }}
      onDestinationChange={(value) => {
        modify(0, "destination", value);
      }}
      onOriginChange={(value) => {
        modify(0, "origin", value);
      }}
      onSwapClick={() => {
        swapOriginDestination(0);
      }}
      origin={origin}
      originIsRequired
      originName="from"
      showValidation={errors.showValidation}
    />

    <div
      className="
        flex flex-col gap-1

        lg:flex-row
      "
      data-show-promo-code={showPromoCode}
    >
      <div className="flex flex-row items-start gap-1" data-fs-element="date">
        <BestFaresRangeDatePicker
          bestFareFinderBaseUrl={bestFareFinderBaseUrl}
          departIsRequired
          destination={destination}
          endDate={endDate}
          headingSlot={
            <InputSelect
              aria-label="trip type"
              {...useSelect({
                initialSelectedKey: "round-trip",
                onSelectionChange: onTripTypeChange,
              })}
              className="text-label font-extrabold"
              label="Trip Type"
              options={[
                { label: labels.tripType.roundTrip, value: "round-trip" },
                { label: labels.tripType.oneWay, value: "one-way" },
              ]}
              value="round-trip" // Resets the InputSelect to its initialSelectedKey on select
              variant="simple"
            />
          }
          isOpen={isOpen}
          labels={{
            close: labels.close,
            depart: labels.departureDate,
            departError: labels.errors.departureDate,
            done: labels.done,
            reset: labels.reset,
            return: labels.returnDate,
            returnError: labels.errors.returnDate,
          }}
          onOpenChange={onOpenChange}
          origin={origin}
          passengers={passengers}
          returnIsRequired
          selectedDates={[departureDate, returnDate]}
          setSelectedDates={[
            (date) => {
              modify(0, "departureDate", date);
            },
            (date) => {
              modify(0, "returnDate", date);
            },
          ]}
          showDatePickerPricing={showDatePickerPricing}
          showValidation={errors.showValidation}
          startDate={startDate}
          toolbarLabels={labels.datePickerToolbar}
          usePoints={usePoints}
        />
        <input
          className="hidden"
          name="return"
          readOnly
          value={returnDate?.toString() ?? ""}
        />
        <input
          className="hidden"
          name="depart"
          readOnly
          value={departureDate?.toString() ?? ""}
        />

        {showPromoCode && (
          <div
            className="
              w-28 min-w-28

              md:w-1/3

              lg:w-28
            "
          >
            <InputText
              data-fs-element="promo-code"
              label={labels.promoCode}
              labelClassName="w-4/5 truncate"
              name="airPromo"
              onChange={(_, value) => {
                setPromoCode(value);
              }}
              value={promoCode}
            />
          </div>
        )}
      </div>

      <div
        className="
          mt-2 flex justify-center text-caption

          md:hidden
        "
      >
        <InputCheckbox
          data-fs-element="points-toggle"
          isSelected={usePoints}
          onChange={() => {
            setUsePoints((current) => !current);
          }}
        >
          {labels.loyaltyCheckbox}
        </InputCheckbox>
      </div>

      <div
        className="
          mt-4 flex justify-center

          lg:mt-0
        "
      >
        <Button
          className="
            h-14 w-full whitespace-nowrap

            md:w-fit md:min-w-unset

            lg:rounded-s-none
          "
          data-fs-element="submit"
          size="large"
          type="submit"
        >
          {labels.submit}
        </Button>
      </div>
    </div>
  </div>
);
